import React, { useState, useMemo, useEffect, useContext } from 'react';
import { 
  DollarSign, 
  Wifi, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ArrowUpDown, 
  ChevronDown, 
  SlidersHorizontal, 
  Plus, 
  RefreshCw, 
  Search, 
  Edit, 
  Trash2,
  Loader,
  AlertTriangle 
} from 'lucide-react';
import Button from '../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../utils/config';
import { AuthContext } from '../../../contexts/AuthContext';

// Utility Categories as per API documentation
const UTILITY_CATEGORIES = [
  { value: 'electricity', label: 'Electricity' },
  { value: 'water', label: 'Water' },
  { value: 'gas', label: 'Gas' },
  { value: 'internet', label: 'Internet' },
  { value: 'phone', label: 'Phone/Mobile' },
  { value: 'waste_management', label: 'Waste Management' },
  { value: 'security', label: 'Security' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'other', label: 'Other' }
];

// Payment Methods as per API documentation
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'online_transfer', label: 'Online Transfer' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'debit_card', label: 'Debit Card' },
  { value: 'auto_debit', label: 'Auto Debit' },
  { value: 'upi', label: 'UPI' },
  { value: 'other', label: 'Other' }
];

// Status Values as per API documentation
const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'yellow' },
  { value: 'paid', label: 'Paid', color: 'green' },
  { value: 'overdue', label: 'Overdue', color: 'red' },
  { value: 'cancelled', label: 'Cancelled', color: 'gray' },
  { value: 'processing', label: 'Processing', color: 'blue' }
];

const Utilities = () => {
  const { user, token } = useContext(AuthContext);

  // State Management
  const [utilities, setUtilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingUtility, setEditingUtility] = useState(null);
  const [utilityPendingDelete, setUtilityPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Filter and Search State
  const [activeTab, setActiveTab] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all-time');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);

  // Form Data State
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    category: '',
    vendor: '',
    amount: '',
    payment_method: 'online_transfer',
    status: 'pending',
    recurring: false,
    notes: ''
  });

  // Fetch utilities from API
  useEffect(() => {
    fetchUtilities();
  }, []);

  const fetchUtilities = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(API_ENDPOINTS.UTILITIES_LIST, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Unauthorized. Please log in again.');
        } else {
          setError(`Failed to fetch utilities: ${response.statusText}`);
        }
        setUtilities([]);
      } else {
        const data = await response.json();
        setUtilities(data.results || data || []);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch utilities');
      setUtilities([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and Sort Utilities
  const filteredUtilities = useMemo(() => {
    let filtered = [...utilities];

    // Tab filter (recurring)
    if (activeTab === 'recurring' && filtered.length > 0) {
      filtered = filtered.filter(u => u.recurring === true);
    } else if (activeTab === 'one-time' && filtered.length > 0) {
      filtered = filtered.filter(u => u.recurring === false);
    }

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        (u.description?.toLowerCase().includes(search)) ||
        (u.vendor?.toLowerCase().includes(search)) ||
        (u.id?.toString().toLowerCase().includes(search))
      );
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(u => u.category === selectedCategory);
    }

    // Status filter
    if (selectedStatus) {
      filtered = filtered.filter(u => u.status === selectedStatus);
    }

    // Period filter
    if (selectedPeriod !== 'all-time' && filtered.length > 0) {
      const today = new Date();
      filtered = filtered.filter(u => {
        if (!u.date) return false;
        const utilityDate = new Date(u.date);

        switch (selectedPeriod) {
          case 'this-month':
            return utilityDate.getMonth() === today.getMonth() &&
              utilityDate.getFullYear() === today.getFullYear();
          case 'last-month': {
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1);
            return utilityDate.getMonth() === lastMonth.getMonth() &&
              utilityDate.getFullYear() === lastMonth.getFullYear();
          }
          case 'this-quarter': {
            const quarter = Math.floor(today.getMonth() / 3);
            const utilityQuarter = Math.floor(utilityDate.getMonth() / 3);
            return utilityQuarter === quarter && utilityDate.getFullYear() === today.getFullYear();
          }
          case 'this-year':
            return utilityDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const key = sortConfig.key;
      let aVal = a[key];
      let bVal = b[key];

      // Handle date sorting
      if (key === 'date') {
        aVal = new Date(aVal) || new Date(0);
        bVal = new Date(bVal) || new Date(0);
      }

      // Handle amount sorting (numeric)
      if (key === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [utilities, activeTab, selectedPeriod, searchTerm, selectedCategory, selectedStatus, sortConfig]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const total = filteredUtilities.reduce((sum, u) => sum + (parseFloat(u.amount) || 0), 0);
    const paid = filteredUtilities
      .filter(u => u.status === 'paid')
      .reduce((sum, u) => sum + (parseFloat(u.amount) || 0), 0);
    const pending = filteredUtilities
      .filter(u => u.status === 'pending')
      .reduce((sum, u) => sum + (parseFloat(u.amount) || 0), 0);
    return { total, paid, pending };
  }, [filteredUtilities]);

  // Form Handlers
  const validateForm = () => {
    const errors = {};

    if (!formData.date) errors.date = 'Date is required';
    if (!formData.description || formData.description.trim().length === 0) {
      errors.description = 'Description is required';
    } else if (formData.description.length > 500) {
      errors.description = 'Description must not exceed 500 characters';
    }

    if (!formData.category) errors.category = 'Category is required';
    if (!formData.vendor || formData.vendor.trim().length === 0) {
      errors.vendor = 'Vendor is required';
    } else if (formData.vendor.length > 200) {
      errors.vendor = 'Vendor must not exceed 200 characters';
    }

    if (!formData.amount) {
      errors.amount = 'Amount is required';
    } else if (isNaN(parseFloat(formData.amount))) {
      errors.amount = 'Amount must be a valid number';
    } else if (parseFloat(formData.amount) < 0) {
      errors.amount = 'Amount must be greater than or equal to 0';
    }

    if (!formData.payment_method) errors.payment_method = 'Payment method is required';
    if (!formData.status) errors.status = 'Status is required';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount).toFixed(2)
      };

      let response;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      if (formMode === 'create') {
        response = await fetch(API_ENDPOINTS.UTILITIES_CREATE, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });
      } else {
        response = await fetch(API_ENDPOINTS.UTILITIES_UPDATE(editingUtility.id), {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        setFormErrors(errorData || { submit: 'Failed to save utility' });
        return;
      }

      // Refresh the utilities list
      await fetchUtilities();

      // Close form and reset
      setIsFormOpen(false);
      setEditingUtility(null);
      setFormMode('create');
      setFormData({
        date: new Date().toISOString().split('T')[0],
        description: '',
        category: '',
        vendor: '',
        amount: '',
        payment_method: 'online_transfer',
        status: 'pending',
        recurring: false,
        notes: ''
      });
      setFormErrors({});
    } catch (err) {
      setFormErrors({ submit: err.message || 'Failed to save utility' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (utility) => {
    setEditingUtility(utility);
    setFormMode('edit');
    setFormData({
      date: utility.date,
      description: utility.description,
      category: utility.category,
      vendor: utility.vendor,
      amount: utility.amount.toString(),
      payment_method: utility.payment_method,
      status: utility.status,
      recurring: utility.recurring || false,
      notes: utility.notes || ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const handleDeleteClick = (utility) => {
    setUtilityPendingDelete(utility);
  };

  const handleConfirmDelete = async () => {
    if (!utilityPendingDelete) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(
        API_ENDPOINTS.UTILITIES_DELETE(utilityPendingDelete.id),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        setError('Failed to delete utility');
        return;
      }

      // Refresh utilities
      await fetchUtilities();
      setUtilityPendingDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete utility');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setUtilityPendingDelete(null);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setEditingUtility(null);
    setFormMode('create');
    setFormData({
      date: new Date().toISOString().split('T')[0],
      description: '',
      category: '',
      vendor: '',
      amount: '',
      payment_method: 'online_transfer',
      status: 'pending',
      recurring: false,
      notes: ''
    });
    setFormErrors({});
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = STATUS_OPTIONS.find(s => s.value === status);
    if (!statusConfig) return null;

    const colorMap = {
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      gray: 'bg-gray-100 text-gray-800',
      blue: 'bg-blue-100 text-blue-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[statusConfig.color] || colorMap.gray}`}>
        {status === 'paid' && <CheckCircle className="w-3 h-3 mr-1" />}
        {status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
        {status === 'overdue' && <AlertCircle className="w-3 h-3 mr-1" />}
        {status === 'processing' && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
        {statusConfig.label}
      </span>
    );
  };

  const getCategoryLabel = (category) => {
    const cat = UTILITY_CATEGORIES.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  const getPaymentMethodLabel = (method) => {
    const pm = PAYMENT_METHODS.find(p => p.value === method);
    return pm ? pm.label : method;
  };

  // Loading State
  if (loading && utilities.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-3">
          <Loader className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading utilities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cashflow Management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage utility bills and recurring expenses</p>
        </div>
        <Button
          onClick={() => {
            setFormMode('create');
            setEditingUtility(null);
            setFormData({
              date: new Date().toISOString().split('T')[0],
              description: '',
              category: '',
              vendor: '',
              amount: '',
              payment_method: 'online_transfer',
              status: 'pending',
              recurring: false,
              notes: ''
            });
            setFormErrors({});
            setIsFormOpen(true);
          }}
          className="flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Utility
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-red-900">{error}</h3>
            <button
              onClick={() => {
                setError(null);
                fetchUtilities();
              }}
              className="mt-1 text-sm font-medium text-red-700 hover:text-red-600"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summaryStats.total)}</p>
              <p className="text-xs text-muted-foreground mt-1">{filteredUtilities.length} utilities</p>
            </div>
            <DollarSign className="w-10 h-10 text-blue-100" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Paid</p>
              <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(summaryStats.paid)}</p>
              <p className="text-xs text-muted-foreground mt-1">{filteredUtilities.filter(u => u.status === 'paid').length} paid</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-100" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1">{formatCurrency(summaryStats.pending)}</p>
              <p className="text-xs text-muted-foreground mt-1">{filteredUtilities.filter(u => u.status === 'pending').length} pending</p>
            </div>
            <Clock className="w-10 h-10 text-yellow-100" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        {/* Search and Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <input
              type="text"
              placeholder="Search by description, vendor, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
          <button
            onClick={fetchUtilities}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="border-t border-gray-200 pt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Categories</option>
                {UTILITY_CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all-time">All Time</option>
                <option value="this-month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="this-quarter">This Quarter</option>
                <option value="this-year">This Year</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All</option>
                <option value="recurring">Recurring</option>
                <option value="one-time">One-time</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Utilities Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {filteredUtilities.length === 0 ? (
          <div className="p-8 text-center">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No utilities found</p>
            <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or create a new utility</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('date')}>
                    <div className="flex items-center gap-2">
                      Date <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Vendor</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-100" onClick={() => requestSort('amount')}>
                    <div className="flex items-center justify-end gap-2">
                      Amount <ArrowUpDown className="w-4 h-4" />
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Payment</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700">Type</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUtilities.map(utility => (
                  <tr key={utility.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-900">{formatDate(utility.date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">{utility.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getCategoryLabel(utility.category)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{utility.vendor}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">{formatCurrency(utility.amount)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getPaymentMethodLabel(utility.payment_method)}</td>
                    <td className="px-6 py-4 text-sm">{getStatusBadge(utility.status)}</td>
                    <td className="px-6 py-4 text-sm text-center">
                      {utility.recurring ? (
                        <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                          Recurring
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                          One-time
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEdit(utility)}
                          className="p-1 hover:bg-blue-50 rounded text-blue-600 hover:text-blue-700"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(utility)}
                          className="p-1 hover:bg-red-50 rounded text-red-600 hover:text-red-700"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {formMode === 'create' ? 'Add New Utility' : 'Edit Utility'}
              </h2>
              <button
                onClick={handleCancelForm}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
              {formErrors.submit && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{formErrors.submit}</p>
                </div>
              )}

              {/* Date and Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.date ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                  />
                  {formErrors.date && <p className="text-xs text-red-600 mt-1">{formErrors.date}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input
                    type="text"
                    placeholder="e.g., Electricity Bill"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.description ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    maxLength="500"
                  />
                  <div className="flex justify-between items-center mt-1">
                    {formErrors.description && <p className="text-xs text-red-600">{formErrors.description}</p>}
                    <p className="text-xs text-muted-foreground">{formData.description.length}/500</p>
                  </div>
                </div>
              </div>

              {/* Category and Vendor */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.category ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                  >
                    <option value="">Select Category</option>
                    {UTILITY_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  {formErrors.category && <p className="text-xs text-red-600 mt-1">{formErrors.category}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vendor *</label>
                  <input
                    type="text"
                    placeholder="e.g., NESCO, Water Department"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.vendor ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.vendor}
                    onChange={(e) => handleFormChange('vendor', e.target.value)}
                    maxLength="200"
                  />
                  {formErrors.vendor && <p className="text-xs text-red-600 mt-1">{formErrors.vendor}</p>}
                </div>
              </div>

              {/* Amount, Payment Method, Status */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.amount ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.amount}
                    onChange={(e) => handleFormChange('amount', e.target.value)}
                  />
                  {formErrors.amount && <p className="text-xs text-red-600 mt-1">{formErrors.amount}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method *</label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.payment_method ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.payment_method}
                    onChange={(e) => handleFormChange('payment_method', e.target.value)}
                  >
                    {PAYMENT_METHODS.map(method => (
                      <option key={method.value} value={method.value}>{method.label}</option>
                    ))}
                  </select>
                  {formErrors.payment_method && <p className="text-xs text-red-600 mt-1">{formErrors.payment_method}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${formErrors.status ? 'border-red-500' : 'border-gray-300'
                      }`}
                    value={formData.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  {formErrors.status && <p className="text-xs text-red-600 mt-1">{formErrors.status}</p>}
                </div>
              </div>

              {/* Recurring and Notes */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    id="recurring"
                    type="checkbox"
                    className="h-4 w-4 text-primary border-gray-300 rounded cursor-pointer"
                    checked={formData.recurring}
                    onChange={(e) => handleFormChange('recurring', e.target.checked)}
                  />
                  <label htmlFor="recurring" className="text-sm font-medium text-gray-700 cursor-pointer">
                    This is a recurring utility (monthly, quarterly, etc.)
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    placeholder="Add any additional notes about this utility..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                    rows="3"
                    value={formData.notes}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelForm}
                  disabled={formLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={formLoading}
                  className="flex items-center gap-2"
                >
                  {formLoading && <Loader className="w-4 h-4 animate-spin" />}
                  {formMode === 'create' ? 'Create Utility' : 'Update Utility'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {utilityPendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg border border-gray-200 w-full max-w-md shadow-xl">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wide text-red-600 font-semibold">
                    Delete Utility
                  </p>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {utilityPendingDelete?.description || 'Confirm deletion'}
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                This action cannot be undone. The utility record will be permanently removed from Cashflow.
              </p>
            </div>
            <div className="p-6 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
                className="flex items-center gap-2"
              >
                {deleteLoading && <Loader className="w-4 h-4 animate-spin" />}
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Utilities;